import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Lock, Mail, AlertTriangle } from 'lucide-react';

function formatApiError(err) {
  // Network / no response at all
  if (err && err.code === 'ERR_NETWORK')
    return "Le serveur ne répond pas (réseau ou serveur indisponible). Réessayez dans quelques secondes.";
  if (err && err.code === 'ECONNABORTED')
    return "Délai de connexion dépassé. Vérifiez votre réseau et réessayez.";

  const resp = err?.response;
  if (!resp && err) {
    return `Connexion au serveur impossible (${err.code || err.message || 'erreur réseau'}).`;
  }
  const detail = resp?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' · ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  if (resp?.status === 502 || resp?.status === 503 || resp?.status === 504)
    return `Serveur momentanément indisponible (${resp.status}). Réessayez.`;
  if (resp?.status) return `Erreur ${resp.status}. Réessayez.`;
  return "Erreur inconnue, réessayez.";
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // If already authenticated, redirect away from the login page.
  // Done in useEffect to avoid setState-in-render warnings.
  useEffect(() => {
    if (user && user !== false && user !== null) {
      const from = location.state?.from || '/';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Email et mot de passe requis');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await login(email.trim().toLowerCase(), password);
      const from = location.state?.from || '/';
      navigate(from, { replace: true });
    } catch (e) {
      // Log le détail complet en console pour pouvoir diagnostiquer en F12 → Console
      // eslint-disable-next-line no-console
      console.error('[Login error]', {
        code: e?.code,
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      });
      setError(formatApiError(e) || 'Erreur de connexion');
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#003580] to-[#004AAD] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center bg-white rounded-2xl mb-4 px-6 py-4 shadow-2xl">
            <img src="/logo.png" alt="Reno Resolve" className="h-16 w-auto" />
          </div>
          <p className="text-blue-100/80 text-sm mt-1">CRM Bâtiment & Collectivités</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl p-8 space-y-5" data-testid="login-form">
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">Connexion</h2>
            <p className="text-sm text-slate-500">Connectez-vous pour accéder au CRM.</p>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="login-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@example.com"
                className="pl-9"
                autoComplete="email"
                data-testid="login-email"
                disabled={busy}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-9"
                autoComplete="current-password"
                data-testid="login-password"
                disabled={busy}
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy || !email || !password}
            className="w-full h-11 text-white"
            style={{ backgroundColor: '#004AAD' }}
            data-testid="login-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {busy ? 'Connexion…' : 'Se connecter'}
          </Button>

          <p className="text-xs text-slate-400 text-center pt-2 border-t border-slate-100">
            Accès réservé. Contactez votre administrateur pour obtenir un compte.
          </p>
        </form>
      </div>
    </div>
  );
}
